import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Wrench, Plus, Trash, Calendar as CalendarIcon, Warning, PencilSimple, ShieldCheck, QrCode } from "@phosphor-icons/react";
import EquipmentQRDialog from "@/components/EquipmentQRDialog";

const KIND_META = {
  vehicle: { label: "Véhicule", icon: Truck, color: "#005CFF" },
  tool: { label: "Outil", icon: Wrench, color: "#F59E0B" },
  other: { label: "Autre", icon: ShieldCheck, color: "#94A3B8" },
};

const DUE_KIND_LABELS = {
  ct: "Contrôle technique",
  revision: "Révision",
  assurance: "Assurance",
  calibrage: "Calibrage",
  vidange: "Vidange",
  controle: "Contrôle périodique",
  autre: "Autre",
};

const SEVERITY = {
  overdue: { color: "#EF4444", label: "En retard" },
  critical: { color: "#F97316", label: "J-7" },
  warning: { color: "#F59E0B", label: "J-30" },
  ok: { color: "#10B981", label: "OK" },
};

function daysRemaining(dueDate) {
  if (!dueDate) return null;
  try {
    const d = new Date(dueDate + "T00:00:00");
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.round((d - now) / 86400000);
  } catch { return null; }
}

function severityOf(days) {
  if (days === null) return "ok";
  if (days < 0) return "overdue";
  if (days <= 7) return "critical";
  if (days <= 30) return "warning";
  return "ok";
}

export default function EquipmentPanel() {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | equipment
  const [dueEditing, setDueEditing] = useState(null); // {equipment}
  const [qrFor, setQrFor] = useState(null);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([api.get("/equipment"), api.get("/equipment/alerts")]);
      setItems(r1.data);
      setAlerts(r2.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { load(); }, []);

  const del = async (eq) => {
    if (!window.confirm(`Supprimer ${eq.name} et toutes ses échéances ?`)) return;
    try { await api.delete(`/equipment/${eq.id}`); await load(); toast.success("Supprimé"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const delDue = async (eq, dd) => {
    if (!window.confirm(`Supprimer l'échéance « ${dd.label || DUE_KIND_LABELS[dd.kind]} » ?`)) return;
    try { await api.delete(`/equipment/${eq.id}/due-dates/${dd.id}`); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="equipment-panel">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-semibold">Outillage & Véhicules</div>
            <div className="text-xs text-white/50 mt-0.5">Inventaire + rappels automatiques à J-30 et J-7 avant chaque échéance.</div>
          </div>
          <Button onClick={() => setEditing("new")} className="bg-[#005CFF] hover:bg-[#0047CC]" data-testid="equipment-add-btn">
            <Plus size={16} /> Ajouter
          </Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4">
          <div className="flex items-center gap-2 text-red-300 font-semibold text-sm mb-2">
            <Warning size={16} weight="fill" /> {alerts.length} échéance{alerts.length > 1 ? "s" : ""} à surveiller
          </div>
          <div className="space-y-1.5">
            {alerts.slice(0, 6).map((a) => {
              const sev = SEVERITY[a.severity] || SEVERITY.ok;
              return (
                <div key={a.id} className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-full" style={{ background: sev.color }} />
                  <span className="font-semibold" style={{ color: sev.color }}>{sev.label}</span>
                  <span className="text-white/80">{a.equipment?.name}</span>
                  <span className="text-white/40">·</span>
                  <span className="text-white/60">{a.label || DUE_KIND_LABELS[a.kind]} — {a.due_date}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-center text-white/40 text-sm py-8">Aucun équipement enregistré.</div>
        )}
        {items.map((eq) => {
          const meta = KIND_META[eq.kind] || KIND_META.other;
          const IconC = meta.icon;
          const next = eq.next_due;
          const days = next ? daysRemaining(next.due_date) : null;
          const sev = SEVERITY[severityOf(days)];
          return (
            <div key={eq.id} className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`equipment-item-${eq.id}`}>
              <div className="p-3 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: meta.color + "22", color: meta.color }}>
                  <IconC size={22} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{eq.name}</div>
                  <div className="text-[11px] text-white/50 truncate">
                    {[eq.brand, eq.model, eq.plate || eq.serial].filter(Boolean).join(" · ") || meta.label}
                  </div>
                </div>
                {next && (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: sev.color }}>{sev.label}</div>
                    <div className="text-[11px] text-white/70">{next.label || DUE_KIND_LABELS[next.kind]}</div>
                    <div className="text-[10px] text-white/40">{next.due_date}</div>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <button onClick={() => setQrFor(eq)} className="text-[#7CB2FF] hover:text-white p-1" title="QR code" data-testid={`equipment-qr-${eq.id}`}><QrCode size={14} /></button>
                  <button onClick={() => setEditing(eq)} className="text-white/50 hover:text-white p-1" data-testid={`equipment-edit-${eq.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => del(eq)} className="text-white/50 hover:text-red-400 p-1" data-testid={`equipment-delete-${eq.id}`}><Trash size={14} /></button>
                </div>
              </div>

              <div className="border-t border-white/10 p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Échéances ({(eq.due_dates || []).length})</div>
                  <button onClick={() => setDueEditing({ equipment: eq })} className="text-[11px] text-[#005CFF] hover:underline flex items-center gap-1" data-testid={`equipment-add-due-${eq.id}`}>
                    <Plus size={11} /> Nouvelle
                  </button>
                </div>
                {(eq.due_dates || []).length === 0 && (
                  <div className="text-[11px] text-white/30">Aucune — ajoutez CT, révision, assurance…</div>
                )}
                {(eq.due_dates || []).map((dd) => {
                  const d = daysRemaining(dd.due_date);
                  const s = SEVERITY[severityOf(d)];
                  return (
                    <div key={dd.id} className="flex items-center gap-2 text-[12px]" data-testid={`equipment-due-${dd.id}`}>
                      <CalendarIcon size={12} className="text-white/40" />
                      <span className="text-white/80">{dd.label || DUE_KIND_LABELS[dd.kind]}</span>
                      <span className="text-white/40">·</span>
                      <span className="text-white/60">{dd.due_date}</span>
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold" style={{ background: s.color + "22", color: s.color }}>
                        {d === null ? "—" : d < 0 ? `+${Math.abs(d)}j` : `J-${d}`}
                      </span>
                      <button onClick={() => delDue(eq, dd)} className="ml-auto text-white/40 hover:text-red-400"><Trash size={11} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {editing && <EditDialog value={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={load} />}
      {dueEditing && <DueDialog equipment={dueEditing.equipment} onClose={() => setDueEditing(null)} onSaved={load} />}
      {qrFor && <EquipmentQRDialog open onClose={() => setQrFor(null)} equipment={qrFor} />}
    </div>
  );
}

function EditDialog({ value, onClose, onSaved }) {
  const [form, setForm] = useState(value || { name: "", kind: "vehicle", brand: "", model: "", plate: "", serial: "", notes: "", assignee_id: null });
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/users").then((r) => setTeam(r.data || [])).catch(() => setTeam([]));
  }, []);

  // Memoise the filtered/mapped team so the Select isn't rebuilt on each parent
  // re-render (also flagged by code review linter).
  const activeTeam = useMemo(() => (team || []).filter((u) => u.is_active !== false), [team]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      // Normalize "none" sentinel back to null before hitting the API
      const payload = { ...form, assignee_id: form.assignee_id === "none" ? null : form.assignee_id };
      if (value?.id) await api.patch(`/equipment/${value.id}`, payload);
      else await api.post("/equipment", payload);
      toast.success("Enregistré");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-md">
        <DialogHeader><DialogTitle>{value?.id ? "Modifier" : "Nouvel équipement"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="equipment-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                <SelectItem value="vehicle">Véhicule</SelectItem>
                <SelectItem value="tool">Outil</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Nom</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Renault Master, Multimètre Fluke…" className="bg-white/5 border-white/10" data-testid="equipment-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Marque</Label><Input value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-white/5 border-white/10" /></div>
            <div><Label className="text-xs">Modèle</Label><Input value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} className="bg-white/5 border-white/10" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Immatriculation</Label><Input value={form.plate || ""} onChange={(e) => setForm({ ...form, plate: e.target.value })} className="bg-white/5 border-white/10" /></div>
            <div><Label className="text-xs">N° série</Label><Input value={form.serial || ""} onChange={(e) => setForm({ ...form, serial: e.target.value })} className="bg-white/5 border-white/10" /></div>
          </div>
          <div>
            <Label className="text-xs">
              {form.kind === "vehicle" ? "Attitré à (conducteur)" : "Attitré à (responsable)"}
            </Label>
            <Select
              value={form.assignee_id || "none"}
              onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? null : v })}
            >
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="equipment-assignee">
                <SelectValue placeholder="Aucun" />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                <SelectItem value="none">— Aucun —</SelectItem>
                {activeTeam.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-white/40 mt-1">
              L&apos;équipier attitré recevra les alertes J-30 / J-7 par notif push + WhatsApp CallMeBot.
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#005CFF] hover:bg-[#0047CC]" data-testid="equipment-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DueDialog({ equipment, onClose, onSaved }) {
  const [form, setForm] = useState({ kind: "ct", label: "", due_date: "", notes: "", create_agenda_event: true });
  const [saving, setSaving] = useState(false);
  const hasAssignee = !!equipment?.assignee_id;

  const save = async () => {
    if (!form.due_date) { toast.error("Date requise"); return; }
    setSaving(true);
    try {
      await api.post(`/equipment/${equipment.id}/due-dates`, form);
      toast.success(form.create_agenda_event ? "Échéance ajoutée + RDV agenda créé" : "Échéance ajoutée");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-neutral-950 border-white/10 max-w-md">
        <DialogHeader><DialogTitle>Nouvelle échéance — {equipment.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Type d&apos;échéance</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="due-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                {Object.entries(DUE_KIND_LABELS).map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Libellé personnalisé (optionnel)</Label>
            <Input value={form.label || ""} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={DUE_KIND_LABELS[form.kind]} className="bg-white/5 border-white/10" />
          </div>
          <div>
            <Label className="text-xs">Date d&apos;échéance / RDV</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-white/5 border-white/10" data-testid="due-date" />
          </div>
          <label className="flex items-start gap-2 cursor-pointer select-none rounded-xl bg-amber-500/10 border border-amber-500/30 p-2.5" data-testid="due-create-agenda-label">
            <input
              type="checkbox"
              checked={form.create_agenda_event}
              onChange={(e) => setForm({ ...form, create_agenda_event: e.target.checked })}
              className="mt-0.5 accent-amber-500"
              data-testid="due-create-agenda"
            />
            <div className="flex-1 text-[11px] text-amber-100/90 leading-relaxed">
              <b className="text-amber-300">Ajouter le RDV à l&apos;agenda</b><br/>
              {hasAssignee
                ? "Une intervention sera créée automatiquement dans l'agenda de l'équipier attitré à cette date."
                : "L'équipement n'a pas de conducteur/responsable attitré. Le RDV sera assigné au gérant. Astuce : attitre-le pour qu'il tombe sur l'agenda du bon équipier."}
            </div>
          </label>
          <div className="text-[11px] text-white/50">Rappel automatique à J-30 et J-7 (push + WhatsApp CallMeBot à l&apos;assignee).</div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#005CFF] hover:bg-[#0047CC]" data-testid="due-save">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
